import { Data, useCreateLayer as useCreateLayerMutation, Variables } from 'hooks/queries/layers/useCreateLayer'
import { showToastMessage } from '../ToastMessage/ShowToastMessage'
import { reportError } from '../../utils/error'
import LayerTabStyles from '../../components/LayerTab/LayerTab.module.scss'
import { useAuth } from '../../hooks/UseAuth'
import { useLayerLocation } from '../../hooks/useLayerLocation'
import { useHistory } from 'react-router-dom'
import { UseMutationOptions } from 'react-query/types/react/types'
import { useCallback } from 'react'

interface Props {
  articleKey: string
}

export const useCreateLayer = (
  { articleKey }: Props,
  options?: Omit<UseMutationOptions<Data, unknown, Variables>, 'mutationKey' | 'mutationFn'>,
) => {
  const { mutate, ...rest } = useCreateLayerMutation(
    { articleKey },
    {
      onError: (error, variables, context) => {
        options?.onError?.(error, variables, context)
        showToastMessage('There was an error creating the layer. Our team has been notified.', 'error')
        reportError(error)
      },
      onSuccess: (data, variables, context) => {
        options?.onSuccess?.(data, variables, context)
        // Open the new layer
        const to = layerLocationFn(data.layer.id)
        history.push(to)

        // Put new layer tab into renaming mode
        const tabs = document.querySelectorAll<HTMLElement>(`.${LayerTabStyles.wrapper}`)
        if (tabs.length > 0) tabs[tabs.length - 1].click()
        showToastMessage('Layer created', 'success')
      },
    },
  )
  const auth = useAuth()
  const layerLocationFn = useLayerLocation()
  const name = `${auth ? auth.first_name : 'Anonymous'}'s Layer`
  const history = useHistory()

  const createLayer = useCallback(
    (
      variables?: Variables,
      options?: Omit<UseMutationOptions<Data, unknown, Variables>, 'mutationKey' | 'mutationFn'>,
    ) => {
      mutate(
        {
          name,
          isPublic: false,
          ...variables,
        },
        options,
      )
    },
    [mutate, name],
  )

  return { createLayer, ...rest }
}
